// Type-only hook imports (erased at build, so the file stays server-safe).
import type { useSetAppLanguageAction } from '@bridebook/web/lib/change-language/use-set-app-language-action';
import type { useInitialiseChecklistAction } from '@bridebook/web/lib/checklist/use-initialise-checklist-action';
import { LIVE_27666_VENUE_INTENT_OPTIONS } from '@bridebook/web/lib/enquiries/intent';
import type { useSendEnquiriesAction } from '@bridebook/web/lib/enquiries/use-send-enquiries-action';
import type {
  useCreateEventAction,
  useDeleteEventAction,
  useInviteGuestsToEventAction,
  useUninviteGuestsFromEventAction,
  useUpdateEventAction,
} from '@bridebook/web/lib/events/client-actions';
import type { useAddToShortlistAction } from '@bridebook/web/lib/shortlist/use-add-to-shortlist-action';
import type { useMarkSupplierBookedAction } from '@bridebook/web/lib/shortlist/use-mark-supplier-booked-action';
import type { useSetWeddingLocationAction } from '@bridebook/web/lib/weddings/use-set-wedding-location-action';
import { getSupportedAppLocales } from '@bridebook/web/lib/change-language/supported-app-locales';
import { z } from 'zod/v4';
import { hookAction } from './definition';
import { HookActionTypes } from './action-types';

// 24h 'HH:mm', with '' as the explicit "not set" — schemas here never use
// `.optional()`.
const timeOrEmpty = z.union([z.literal(''), z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/)]);

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

// A location query is one place term. Two places or a question is context for the reply,
// never a write: the picker opens for one named place only. A comma stays allowed, because
// "Puglia, Italy" is one place.
const SEVERAL_OR_QUESTION = /\bor\b|\?/i;

const eventId = z
  .string()
  .min(1)
  .describe(
    "The event's exact id copied from planningSnapshot's events — NEVER invent or guess an id.",
  );

const eventGuests = z
  .array(z.string().min(1))
  .min(1)
  .describe(
    'The exact ids of every guest to move, copied from findGuests — ALWAYS batched into this one action, and NEVER invented or guessed. The couple sees their names on the approval card; the app reads them from its own records, so ids are all you send.',
  );

// Events.MAIN_EVENT, inlined: the models class carries firebase and this file
// must stay server-safe.
const MAIN_EVENT_ID = '6da8cf10-0000-4000-8000-000000000000';

// The per-field edits every event owns; null keeps the current value.
const eventUpdateFields = {
  name: z.union([z.string().min(1).max(100), z.null()]).describe('New name, or null to keep.'),
  description: z
    .union([z.string().max(2000), z.null()])
    .describe("New description ('' clears it), or null to keep."),
  startTime: z
    .union([timeOrEmpty, z.null()])
    .describe("New 24h 'HH:mm' start time ('' clears it), or null to keep."),
  endTime: z
    .union([timeOrEmpty, z.null()])
    .describe("New 24h 'HH:mm' end time ('' clears it), or null to keep."),
  featured: z
    .union([z.boolean(), z.null()])
    .describe('Whether the event shows on the wedding website, or null to keep.'),
};

/**
 * Hook lexicon actions, not Redux. Each type must be added to the hook executor map (its type
 * errors until it is), where a domain React hook runs it: React Query mutations, captcha,
 * conversation state. Each reports its hook's return value as `data`, with no declared outcome.
 */
export const HOOK_CLIENT_ACTIONS = [
  hookAction<typeof useSetWeddingLocationAction>()({
    type: HookActionTypes.SET_WEDDING_LOCATION,
    // The hook ends in an ordinary wedding-field write, echo included. The receipt card
    // runs it on the couple's pick, or on a name the resolver settled.
    description:
      "Set where the couple's wedding is, from the one place they named. The query is their own words; the place written is the one they pick, returned as `savedLocation`.",
    access: 'open',
    authoredBy: 'server',
    schema: z.strictObject({
      query: z
        .string()
        .min(1)
        .refine((query) => !SEVERAL_OR_QUESTION.test(query), {
          message:
            'One place term only: no "or", no question. Several places, a region or a question are context to respond to, not a location to save.',
        })
        .describe('The place the couple named, in their own words. One place term only.'),
    }),
  }),
  hookAction<typeof useSendEnquiriesAction>()({
    type: HookActionTypes.SEND_ENQUIRIES,
    // React Query onSuccess reports the send; the enquiry pipeline
    // auto-shortlists contacted venues.
    description:
      "SEND a real enquiry to ONE venue from this conversation's search results or a name lookup, with a message you draft for the couple. This contacts the venue for real: use it only when the couple explicitly asks to enquire, and ONLY to the venue they chose or named — never to another result they didn't pick. When they name a venue that is not in this conversation's results, look it up with searchSupplierByName first, however they spell it, then call this with the id it returns. A name is the business's own name, never a plain word such as \"a venue\" or \"the barn\". One venue per call and one card at a time: when they ask to contact several venues at once, look up each name first, one searchSupplierByName call per name, then enquire with the first. Once its card is settled, sent, declined or refused, enquire with the next in the same reply, until each venue they named has had its card. Never two enquiry calls at once. Once the last card is settled, say in a sentence or two what happened with each venue, only as the card results say. When they ask again for a venue this conversation says was enquired or contacted before, call this again: only the card's result says whether it went, so never say from memory that a venue was contacted. One card per venue per request: a card that was declined or refused is the answer for that venue in this reply, so never call this again for it in the same reply, and never while a name lookup is waiting for them to say which venue they mean. Ask which venue they mean only when they name none, or the lookup finds several that could be it.",
    access: 'consent',
    // Real messages leave the app in the couple's name, so every send needs its own
    // approval: the couple cannot set this to allow.
    alwaysAllow: false,
    schema: z.strictObject({
      suppliers: z
        .array(
          z.strictObject({
            id: z
              .string()
              .min(1)
              .describe("The venue's id from this conversation's search results or a name lookup."),
            name: z
              .string()
              .min(1)
              .describe(
                "The venue's name exactly as the search results returned it — shown to the couple on the approval card.",
              ),
          }),
        )
        // One venue per enquiry: the couple approves and reviews each on its own card.
        .length(1)
        .describe(
          'ONLY the one venue the couple explicitly asked to contact, never another result. Ask when it is unclear which venue they mean.',
        ),
      // The venue form's pills; the executor drops what the supplier's own form lacks.
      intent: z
        .array(z.enum(LIVE_27666_VENUE_INTENT_OPTIONS))
        .min(1)
        .max(LIVE_27666_VENUE_INTENT_OPTIONS.length)
        .optional()
        .describe(
          'The enquiry form\'s checkboxes, ticked from the couple\'s own words. Tick every one their ask touches: `brochure` is "General info": any details or information about the venue, whether or not they say brochure; `pricing` is prices or packages; `availability` is their wedding date; `showround` is a visit or viewing (venues only). "More details" or "tell us about" ticks `brochure` even when they say they want more than a brochure. Always set it; a plain "enquire" with nothing more is `brochure`.',
        ),
      message: z
        .string()
        .min(20)
        .max(2000)
        .describe(
          "The enquiry message written on the couple's behalf: a short, polite note with their wedding date/guest count when known, asking for what `intent` names.",
        ),
    }),
  }),
  hookAction<typeof useCreateEventAction>()({
    type: HookActionTypes.CREATE_EVENT,
    // The hook wraps the events page's React Query upsert; the snapshot's
    // events section refreshes through the global Firestore listener.
    description:
      "Create an additional wedding event — rehearsal dinner, welcome drinks, after-party, day-after brunch. NOT the main wedding day, which this never creates. The result hands back the new event's `eventId`. Nothing else knows that id yet, so when the couple also wants guests invited to the event you are creating, create it FIRST and invite in a SEPARATE later call using the returned id — never guess the id or reuse another event's.",
    access: 'consent',
    schema: z.strictObject({
      name: z
        .string()
        .min(1)
        .max(100)
        .describe("The event's display name, e.g. 'Rehearsal dinner'."),
      date: isoDate.describe('The event date as YYYY-MM-DD, today or later.'),
      description: z
        .string()
        .max(2000)
        .describe("Description shown with the event, '' when the couple gave none."),
      startTime: timeOrEmpty.describe("24h 'HH:mm' start time, '' when not given."),
      endTime: timeOrEmpty.describe("24h 'HH:mm' end time, '' when not given — needs a startTime."),
      venueId: z
        .string()
        .min(1)
        .describe(
          'Id of the supplier hosting the event. Held at their booked venue or another of their suppliers: take the id from planningSnapshot. Held anywhere else: use the id from a venue search in this conversation — a venue they have not shortlisted yet is fine, creating the event adds it to their suppliers. If you cannot look that venue up, ask them to add it to their suppliers first. Never invent an id.',
        ),
      featured: z
        .boolean()
        .describe(
          "Whether the event shows on the couple's wedding website. true unless they say otherwise.",
        ),
    }),
  }),
  hookAction<typeof useUpdateEventAction>()({
    type: HookActionTypes.UPDATE_EVENT,
    description:
      'Edit a wedding event by id. Every `updates` field is null to keep the current value — send only what the couple asked to change. The main wedding day takes the variant without date and venueId: its date is the wedding date (the wedding-date action changes that) and its venue is the booked venue. An additional event keeps the rules its own page has: its date must be today or later, and its venue must be a supplier of theirs or one this conversation looked up.',
    access: 'consent',
    schema: z.union([
      // The wedding owns the main event's date and venue, so its shape simply
      // has neither.
      z.strictObject({
        eventId: z.literal(MAIN_EVENT_ID).describe('The main wedding day.'),
        updates: z.strictObject(eventUpdateFields),
      }),
      z.strictObject({
        eventId: eventId.refine(
          (id) => id !== MAIN_EVENT_ID,
          'The main wedding day takes the variant without date and venueId.',
        ),
        updates: z.strictObject({
          ...eventUpdateFields,
          date: z
            .union([isoDate, z.null()])
            .describe('New date as YYYY-MM-DD, today or later, or null to keep.'),
          venueId: z
            .union([z.string().min(1), z.null()])
            .describe(
              "Id of the supplier now hosting the event — from planningSnapshot's suppliers, or from looking the venue up by name with searchSupplierByName. Null to keep the current one.",
            ),
        }),
      }),
    ]),
  }),
  hookAction<typeof useDeleteEventAction>()({
    type: HookActionTypes.DELETE_EVENT,
    description:
      'Delete a wedding event by id. An event that still has invited guests refuses until they are uninvited (uninviteGuestsFromEvent). Uninvite them in a SEPARATE earlier call and wait for its result — batched together with the delete, the delete still sees them invited and refuses.',
    access: 'consent',
    schema: z.strictObject({
      eventId,
    }),
  }),
  hookAction<typeof useInviteGuestsToEventAction>()({
    type: HookActionTypes.INVITE_GUESTS_TO_EVENT,
    description:
      "Invite guests to a wedding event, by guest id from findGuests and event id from planningSnapshot. Newly invited guests start as 'pending'; a guest already invited keeps their existing answer and comes back as skipped, so re-running is safe.",
    access: 'consent',
    schema: z.strictObject({
      eventId,
      guests: eventGuests,
    }),
  }),
  hookAction<typeof useUninviteGuestsFromEventAction>()({
    type: HookActionTypes.UNINVITE_GUESTS_FROM_EVENT,
    description:
      "Remove guests from a wedding event, by guest id from findGuests and event id from planningSnapshot. Removing an invitation discards that guest's RSVP answer for the event; their other events are untouched.",
    access: 'consent',
    schema: z.strictObject({
      eventId,
      guests: eventGuests,
    }),
  }),
  hookAction<typeof useSetAppLanguageAction>()({
    type: HookActionTypes.SET_APP_LANGUAGE,
    // The hook is the settings language flow verbatim: it persists the user's
    // locale, swaps the i18n language and follows the market URL prefix.
    description:
      'Switch the display language of the whole Bridebook app to one of the supported locales the schema lists. This changes the app UI only — it is NOT a request to change the conversation language, so keep replying in whatever language the couple writes.',
    // An app-wide language flip mid-conversation is disorienting when
    // unexpected, and undoing it means finding the settings screen in a
    // language the couple may not read.
    access: 'consent',
    schema: z.strictObject({
      locale: z
        .enum(getSupportedAppLocales() as [string, ...string[]])
        .describe('The app language, as one of the listed Bridebook market locales.'),
    }),
  }),
  hookAction<typeof useAddToShortlistAction>()({
    type: HookActionTypes.ADD_TO_SHORTLIST,
    // Visible on their shortlist and undone with one tap there, so no consent gate: a
    // confirm card would ask them to approve the venue they just said they liked.
    description:
      "Add a venue or supplier from this conversation's results to the couple's shortlist. Use it when they say they like one, want to keep it, or ask to save it — ONLY for a supplier they named, never for other results. Pass the id and exact name from the results. Reports `alreadyShortlisted` when it was there already.",
    access: 'open',
    schema: z.strictObject({
      supplierId: z
        .string()
        .min(1)
        .describe(
          "The supplier's exact id from this conversation's results — NEVER invent or guess an id.",
        ),
      supplierName: z
        .string()
        .min(1)
        .describe("The supplier's name exactly as the results list it — shown to the couple."),
    }),
  }),
  hookAction<typeof useMarkSupplierBookedAction>()({
    type: HookActionTypes.MARK_SUPPLIER_BOOKED,
    // Booking moves money in their budget and, for a venue, sets the wedding location. The
    // app's own path confirms it in a popup, so the card asks the same way.
    description:
      "Mark a venue or supplier as BOOKED on the couple's record — it moves from their shortlist to their booked suppliers, and a booked venue sets their wedding location. Use it when they say they have booked, confirmed or signed with a supplier that is on their shortlist, in this conversation's results or found by a name lookup, or when they ask you to mark one as booked. ONLY for a supplier they named; pass the id and exact name from the shortlist, results or lookup. It records the booking only — no price, deposit or date — so never say those were saved. Reports `alreadyBooked` when it was booked already. A venue is booked for one day: when their record has no exact wedding date (day, month and year), ask for it first; when their message gives it, pass it as weddingDate and book now.",
    access: 'consent',
    // A booking is rare and one at a time, so a standing allow would never be used.
    alwaysAllow: false,
    schema: z.strictObject({
      supplierId: z
        .string()
        .min(1)
        .describe(
          "The supplier's exact id from the couple's shortlist, this conversation's results or a name lookup — NEVER invent or guess an id.",
        ),
      supplierName: z
        .string()
        .min(1)
        .describe(
          "The supplier's name exactly as the record, results or lookup list it — shown to the couple.",
        ),
      weddingDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable()
        .optional()
        .describe(
          'A venue only: the exact wedding date (YYYY-MM-DD) the couple gave in this message, when their record has none yet. Null otherwise. It is saved from their message when the turn ends.',
        ),
    }),
  }),
  hookAction<typeof useInitialiseChecklistAction>()({
    type: HookActionTypes.INITIALISE_CHECKLIST,
    description:
      "Build the couple's checklist from the date on file. Rides the turn receipt with a date write, or as repair when a stated date still has no checklist. Never un-ticks a completed task; reports `taskCount`.",
    access: 'open',
    authoredBy: 'server',
    schema: z.strictObject({
      // A repair exists for a checklist that was never built. When one already exists, the
      // record the server read was behind the browser's, and the repair does nothing.
      repair: z.boolean().describe('True when sent without a date write, as repair.'),
    }),
  }),
] as const;
